import { errorIsStatus, useClientPortalAPI } from "@certn/api";
import { BasicButton } from "@certn/cascadia";
import {
  ColumnDefinition,
  Container,
  DataTable,
  Pagination,
  SectionMessage,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { FilterBar } from "../../components/filters/FilterBar";
import { PageTitle } from "../../components/page-title/PageTitle";
import { useQueryParams } from "../../hooks/useQueryParams";
import { Schemas } from "../../types";
import { queryParamsToApiParams } from "../utils";
import { ExperienceVerificationActionsDropdown } from "./ExperienceVerificationActionsDropdown";
import { getCommonTableColumns } from "../templates/utils";

export const ExperienceVerificationTemplatesTable = () => {
  const navigate = useNavigate();
  const { params, setParam } = useQueryParams();
  const { t } = useTranslation();

  const { data, isLoading, error, mutate } = useClientPortalAPI(
    "/api/internal/client-portal/check-arguments-templates/experience-verification/",
    {
      ...queryParamsToApiParams(params),
    }
  );

  const activeTemplates = data
    ? data.results.filter((template) => template.is_active === true)
    : [];

  const columns: ColumnDefinition<
    Schemas["ExperienceVerificationTemplateList"]
  >[] = getCommonTableColumns<Schemas["ExperienceVerificationTemplateList"]>({
    actions: ({ row }) => (
      <ExperienceVerificationActionsDropdown
        id={row.id}
        isActive={row.is_active}
        hideDeactivate={activeTemplates.length === 1}
        updateTemplates={mutate}
      />
    ),
    translate: t,
  });

  return (
    <Container layout="full-width">
      <PageTitle
        actions={
          <BasicButton
            testID="new-template-button"
            onPress={() =>
              navigate(`/settings/experience-verification-templates/create`)
            }
          >
            {t("New template")}
          </BasicButton>
        }
      >
        {t("Experience verification")}
      </PageTitle>
      {error && !errorIsStatus(error, 404) && (
        <SectionMessage
          type="danger"
          icon="error"
          sectionTitle={t(
            "Unable to retrieve experience verification templates"
          )}
        >
          {String(error.message)}
        </SectionMessage>
      )}
      <FilterBar testID="experience-verification-templates" hasSearch />
      <DataTable
        isLoading={isLoading}
        onSortChange={(sortDescriptor) =>
          setParam("ordering", sortDescriptorToOrderingParam(sortDescriptor))
        }
        sortDescriptor={orderingParamToSortDescriptor(params.ordering)}
        columns={columns}
        rows={data?.results ? data.results : []}
        aria-label={t("Experience Verification Templates Table")}
        testId="experience-verification-template-list-view-data-table"
        onRowAction={(id) => {
          navigate(`${id}`);
        }}
      />
      <Pagination
        setPage={(page) => setParam("page", page.toString())}
        setPageCount={(pageCount) =>
          setParam("page_size", pageCount.toString())
        }
        total={data?.count ?? 0}
        page={params.page ? Number(params.page) : 1}
        pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
      />
    </Container>
  );
};
